import { ready } from '~/core'
import addEvents from '~/utils/addEvents'
import bodyLock from '~/utils/bodyLock'
import createElement from '~/utils/createElement'

const modalStatus = {
  isOpen: false,
  active: null,
}

const modalHandler = new Proxy(modalStatus, {
  set(target, prop, [modalElement, id]) {
    const params = new URLSearchParams(window.location.search)

    // clear out previous modal if its already open
    if (target.isOpen && target.active !== modalElement) {
      bodyLock.unlock(target.active)
      document.body.removeChild(target.active)
    }

    // if trying to open
    if (prop === 'openModal') {
      bodyLock.lock(modalElement)
      document.body.appendChild(modalElement)
      if (id) {
        params.set('id', id)
      } else {
        params.delete('id')
      }

      target.isOpen = true
      target.active = modalElement
    }
    // if trying to close
    else if (prop === 'closeModal') {
      bodyLock.unlock(modalElement)
      document.body.removeChild(modalElement)

      params.delete('id')

      target.isOpen = false
      target.active = null
    }

    if (params.toString()) {
      history.replaceState(null, null, `?${params.toString()}`)
    } else {
      history.replaceState({}, '', window.location.origin + window.location.pathname)
    }

    return true
  },
})

ready('video-modal', '.modal--video', (trigger) => {
  let container = trigger.closest('.mediaBlock')
  let mediaUrl, media, modalCategory, modalHeading

  if (container) {
    media = container.querySelector('.mediaBlock-media')
    mediaUrl = media.dataset.videourl
    modalCategory = container.querySelector('.mediaBlock-category')
    modalHeading = container.querySelector('.mediaBlock-heading')
  } else {
    mediaUrl = trigger.dataset.videourl
  }

  // create dynamic elements
  let modalBg = createElement('div', { class: 'modal-bg' })
  let modalInner = createElement('div', { class: 'modal-inner', tabindex: '0' })

  let frame = createElement('iframe', {
    class: 'iframe',
    src: mediaUrl,
    frameborder: 0,
    allowfullscreen: 'allowfullscreen',
  })
  frame.setAttribute('enablejsapi', 'true')

  let ariaLabel = ''
  try {
    ariaLabel = config.modal.CloseButtonText
  } catch {
    ariaLabel = ''
  }

  let modalClose = createElement('button', { class: 'modal-close ico-close', 'aria-label': ariaLabel })

  let mediaWrapper = createElement('div', { class: 'modal-media' })

  let modalBody

  if (container) {
    modalBody = createElement(
      'div',
      { class: 'modal-body' },
      `
        <div class="modal-category">${modalCategory.textContent}</div>
        <div class="modal-heading">${modalHeading.textContent}</div>
      `,
    )
  }

  modalInner.appendChild(modalClose)
  modalBg.appendChild(modalInner)
  modalInner.appendChild(mediaWrapper)

  if (container) modalInner.appendChild(modalBody)

  mediaWrapper.appendChild(frame)
  const focusTrap = createElement('div', { class: 'focus-trap', tabindex: 0, style: 'visability: hidden' })
  modalInner.appendChild(focusTrap)

  frame.setAttribute(
    'allow',
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
  )
  frame.setAttribute('allowfullscreen', '')

  const id = trigger.getAttribute('id')

  const handleKeydownEventsCallback = (e) => {
    const isTab = e.key.toLowerCase() === 'tab'

    if (!isTab) return

    const focusableElements = 'a, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    const focusElements = [...modalInner.querySelectorAll(focusableElements)]

    const firstElement = focusElements[0]

    if (e.target === firstElement && e.shiftKey) {
      e.preventDefault()
      focusTrap.focus()
    }

    if (e.target === focusTrap) {
      if (e.shiftKey) return

      e.preventDefault()
      firstElement.focus()
    }
  }

  // open modal function
  let triggerModal = () => {
    modalHandler.openModal = [modalBg, id]

    modalInner.focus()
    window.addEventListener('keydown', handleKeydownEventsCallback)
  }

  // close modal function
  let closeModal = () => {
    modalHandler.closeModal = [modalBg, id]

    window.removeEventListener('keydown', handleKeydownEventsCallback)
  }

  const doOpen = new CustomEvent('doOpen')

  // events to triger and close modal
  addEvents(trigger, 'click.prevent', triggerModal)
  addEvents(trigger, 'doOpen', triggerModal)
  addEvents(modalClose, 'click.prevent', closeModal)
  addEvents(modalBg, 'click.self', closeModal)

  const params = new URLSearchParams(window.location.search)

  if ([...params.keys()].length) {
    if (params.has('id') && params.get('id') === id) {
      trigger.dispatchEvent(doOpen)
    }
  }
})

ready('calculator-list-modal', '.calculator-list__modal-trigger', (trigger) => {
  let iframeUrl = trigger.dataset.iframeurl
  let iframeTitle = trigger.dataset.iframetitle

  // create dynamic elements
  let modalBg = createElement('div', { class: 'modal-bg' })
  let modalInner = createElement('div', { class: 'modal-inner', tabindex: '0' })
  let frame = createElement('iframe', {
    class: 'iframe',
    src: iframeUrl,
    frameborder: 0,
    allowfullscreen: 'allowfullscreen',
    title: iframeTitle,
  })

  let modalClose = createElement('button', {
    class: 'modal-close ico-close',
    'aria-label': config !== undefined ? config.modal.CloseButtonText : '',
  })

  let iframeHeading = createElement('h2', { class: 'modal-heading' })
  iframeHeading.textContent = iframeTitle

  let iframeWrapper = createElement('div', { class: 'modal-iframe' })
  let modalBody = createElement('div', { class: 'modal-body' })

  modalBg.appendChild(modalInner)
  modalInner.appendChild(modalClose)
  modalInner.appendChild(iframeHeading)
  modalInner.appendChild(iframeWrapper)
  iframeWrapper.appendChild(frame)
  frame.setAttribute(
    'allow',
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
  )
  frame.setAttribute('allowfullscreen', '')

  const focusTrap = createElement('div', { class: 'focus-trap', tabindex: 0, style: 'visability: hidden' })
  iframeWrapper.appendChild(focusTrap)
  modalInner.insertAdjacentElement('beforeend', modalBody)

  const handleKeydownEventsCallback = (e) => {
    const isTab = e.key.toLowerCase() === 'tab'

    if (!isTab) return

    const focusableElements = 'a, button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    const focusElements = [...modalInner.querySelectorAll(focusableElements)]

    const firstElement = focusElements[0]

    if (e.target === firstElement && e.shiftKey) {
      e.preventDefault()
      focusTrap.focus()
    }

    if (e.target === focusTrap) {
      if (e.shiftKey) return

      e.preventDefault()
      firstElement.focus()
    }
  }

  // open modal function
  let openModal = (e) => {
    document.body.appendChild(modalBg)
    bodyLock.lock(trigger)

    modalInner.focus()
    window.addEventListener('keydown', handleKeydownEventsCallback)
  }

  // close modal function
  let closeModal = () => {
    bodyLock.unlock(trigger)
    document.body.removeChild(modalBg)
    window.removeEventListener('keydown', handleKeydownEventsCallback)
    trigger.focus()
  }

  addEvents(trigger, 'click.prevent', openModal)
  addEvents(modalClose, 'click.prevent', closeModal)
  addEvents(modalBg, 'click.self', closeModal)
})

ready('tab-card-menu-card-modal-video', '.tab-card-menu__card--modal--video', (trigger) => {
  let container = trigger
  let mediaUrl, media, modalCategory, modalHeading

  if (container) {
    media = container.querySelector('.tab-card-menu__card')
    mediaUrl = media.dataset.videourl

    modalCategory = container.querySelector('.tab-card-menu__card__media-category')
    modalHeading = container.querySelector('.tab-card-menu__card-label')
  } else {
    mediaUrl = trigger.dataset.videourl
  }

  // create dynamic elements
  let modalBg = createElement('div', {
    class: 'modal-bg',
  })
  let modalInner = createElement('div', {
    class: 'modal-inner',
  })
  let frame = createElement('iframe', {
    class: 'iframe',
    src: mediaUrl,
    frameborder: 0,
    allowfullscreen: 'allowfullscreen',
  })
  frame.setAttribute('enablejsapi', 'true')
  let ariaLabel = ''
  try {
    ariaLabel = config.modal.CloseButtonText
  } catch {
    ariaLabel = ''
  }
  let modalClose = createElement('button', {
    class: 'modal-close ico-close',
    'aria-label': ariaLabel,
  })
  let mediaWrapper = createElement('div', {
    class: 'modal-media',
  })
  let modalBody
  if (container) {
    modalBody = createElement(
      'div',
      {
        class: 'modal-body',
      },
      `
      <div class="modal-category">${modalCategory.textContent}</div>
      <div class="modal-heading">${modalHeading.textContent}</div>
    `,
    )
  }

  modalBg.appendChild(modalInner)
  modalInner.appendChild(mediaWrapper)
  modalInner.appendChild(modalClose)
  if (container) modalInner.appendChild(modalBody)
  mediaWrapper.appendChild(frame)
  frame.setAttribute(
    'allow',
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
  )
  frame.setAttribute('allowfullscreen', '')

  const id = trigger.getAttribute('id')

  // open modal function
  let triggerModal = () => (modalHandler.openModal = [modalBg, id])

  // close modal function
  let closeModal = () => (modalHandler.closeModal = [modalBg, id])

  const doOpen = new CustomEvent('doOpen')

  // events to triger and close modal
  addEvents(trigger, 'click.prevent', triggerModal)
  addEvents(trigger, 'doOpen', triggerModal)
  addEvents(modalClose, 'click.prevent', closeModal)
  addEvents(modalBg, 'click.self', closeModal)

  const params = new URLSearchParams(window.location.search)

  if ([...params.keys()].length) {
    if (params.has('id') && params.get('id') === id) {
      trigger.dispatchEvent(doOpen)
    }
  }
})
